import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Input,
  InputNumber,
  Modal,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  DeleteOutlined,
  FileExcelOutlined,
  FileProtectOutlined,
  PictureOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageTitle from 'components/page-title';
import { useCurrentRoles } from 'hooks/use-current-roles';
import {
  CONDITION_LABEL,
  DEFAULT_SETTINGS,
  formatVnd,
  lineAmounts,
  normalizeQuoteData,
  PersonInfo,
  suggestContractNo,
  priceText,
  QUOTE_STATUSES,
  QuoteData,
  QuoteLine,
  QuoteSettings,
  QuoteStatus,
  summarize,
} from './calc';
import {
  EquipmentItem,
  equipmentApi,
  errorText,
  imageUrl,
  QuoteView,
  STATUS_COLOR,
  STATUS_LABEL,
  triggerDownload,
} from './api';
import ContractModal from './components/contract-modal';
import HandoverTab from './components/handover-tab';
import NoAccess from './components/no-access';
import { renderQuoteImage } from './quote-image';
import styles from './style.module.scss';

const { Text } = Typography;
let keySeq = 0;
const newKey = () => `k${Date.now().toString(36)}${(keySeq++).toString(36)}`;

const money = (v: number | null, onChange: (n: number) => void, width = 150) => (
  <InputNumber<number>
    min={0}
    step={10000}
    value={v ?? 0}
    style={{ width }}
    formatter={(n) => `${n ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
    parser={(s) => Number((s || '').replace(/\D/g, '')) || 0}
    addonAfter="đ"
    onChange={(n) => onChange(Number(n) || 0)}
  />
);

const PersonFields = ({
  value,
  onChange,
  guardian,
}: {
  value: PersonInfo;
  onChange: (p: PersonInfo) => void;
  guardian?: boolean;
}) => {
  const f = (k: keyof PersonInfo, label: string, span = 12, type?: string) => (
    <Col xs={24} md={span} key={k}>
      <div className={styles.fieldLabel}>{label}</div>
      <Input
        type={type}
        value={value[k] || ''}
        onChange={(e) => onChange({ ...value, [k]: e.target.value })}
      />
    </Col>
  );
  return (
    <Row gutter={[12, 10]}>
      {f('fullName', 'Họ và tên')}
      {f('dob', 'Ngày sinh', 6, 'date')}
      {f('phone', 'Điện thoại', 6)}
      {f('idNumber', 'Số CCCD', 8)}
      {f('idIssuedDate', 'Ngày cấp', 6, 'date')}
      {f('idIssuedPlace', 'Nơi cấp', 10)}
      {f('address', 'Địa chỉ thường trú', 24)}
      {guardian
        ? f('relation', 'Quan hệ với Creator (bố, mẹ, người giám hộ)', 12)
        : f('email', 'Email', 8)}
      {!guardian && f('tiktokId', 'TikTok ID', 8)}
      {!guardian && f('tiktokUid', 'UID TikTok', 8)}
    </Row>
  );
};

type PayMode = 'FREE' | 'ADVANCE' | 'SUPPORT' | 'CREATOR';
const PAY_MODES: { value: PayMode; label: string; color: string }[] = [
  { value: 'FREE', label: 'Tặng kèm · miễn phí', color: 'magenta' },
  { value: 'ADVANCE', label: 'Ứng trước · Creator trả lại', color: 'orange' },
  { value: 'SUPPORT', label: 'Công ty hỗ trợ %', color: 'green' },
  { value: 'CREATOR', label: 'Creator tự trả 100%', color: 'default' },
];

/** Hình thức của một dòng — suy từ included / advance / supportPercent. */
function payModeOf(l: QuoteLine): PayMode {
  if (l.advance) return 'ADVANCE';
  if (l.included) return 'FREE';
  return l.supportPercent > 0 ? 'SUPPORT' : 'CREATOR';
}

function patchForMode(m: PayMode, defaultPercent: number): Partial<QuoteLine> {
  if (m === 'FREE') return { included: true, advance: false, supportPercent: 100 };
  if (m === 'ADVANCE') return { included: false, advance: true, supportPercent: 0 };
  if (m === 'CREATOR') return { included: false, advance: false, supportPercent: 0 };
  return {
    included: false,
    advance: false,
    supportPercent: defaultPercent > 0 ? defaultPercent : 100,
  };
}

const EquipmentQuoteEditor = () => {
  const { id = 'new' } = useParams();
  const navigate = useNavigate();
  const { loaded, canUseEquipmentQuote, displayName } = useCurrentRoles();
  const isNew = id === 'new';
  const [settings, setSettings] = useState<QuoteSettings>(DEFAULT_SETTINGS);
  const [quote, setQuote] = useState<QuoteView | null>(null);
  const [data, setData] = useState<QuoteData | null>(null);
  const [items, setItems] = useState<EquipmentItem[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);

  useEffect(() => {
    if (!canUseEquipmentQuote) return;
    equipmentApi
      .items()
      .then(setItems)
      .catch(() => setItems([]));
    if (isNew) {
      Promise.all([
        equipmentApi.settings(),
        equipmentApi.items().catch(() => [] as EquipmentItem[]),
      ])
        .then(([s, list]) => {
          setSettings(s);
          // Món có sẵn trong mọi gói: "Tặng kèm" (Creator HP Media miễn phí) và "Ứng trước" (Creator trả lại).
          const included = list
            .filter((it) => it.includeMode === 'FREE' || it.includeMode === 'ADVANCE')
            .map((it) => lineOf(it, 100));
          setData(
            normalizeQuoteData(
              {
                lines: included,
                hpCreator: true,
                notes: s.notes,
                signDate: new Date().toISOString().slice(0, 10),
              },
              s
            )
          );
        })
        .catch((e) => setError(errorText(e)));
    } else {
      equipmentApi
        .quote(Number(id))
        .then((r) => {
          setSettings(r.settings);
          setQuote(r.quote);
          setData(r.quote.data!);
        })
        .catch((e) => setError(errorText(e)));
    }
  }, [canUseEquipmentQuote, id, isNew]);

  // Rời trang khi còn thay đổi chưa lưu.
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const update = useCallback((patch: Partial<QuoteData>) => {
    setData((d) => (d ? { ...d, ...patch } : d));
    setDirty(true);
  }, []);
  const setLine = (key: string, patch: Partial<QuoteLine>) =>
    data && update({ lines: data.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) });

  const summary = useMemo(() => (data ? summarize(data) : null), [data]);
  const locked = quote?.status === 'SIGNED';

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const map = new Map<string, EquipmentItem[]>();
    for (const it of items) {
      if (q && !`${it.name} ${it.category}`.toLowerCase().includes(q)) continue;
      map.set(it.category, [...(map.get(it.category) ?? []), it]);
    }
    return Array.from(map.entries());
  }, [items, search]);

  const lineOf = (it: EquipmentItem, supportPercent: number): QuoteLine => ({
    key: newKey(),
    itemId: it.id,
    category: it.category,
    name: it.name,
    brand: it.brand || '',
    unit: it.unit,
    price: it.price,
    qty: 1,
    supportPercent:
      it.includeMode === 'FREE' ? 100 : it.includeMode === 'ADVANCE' ? 0 : supportPercent,
    imageKey: it.imageKey || '',
    condition: 'NEW',
    included: it.includeMode === 'FREE',
    advance: it.includeMode === 'ADVANCE',
  });

  const addItem = (it: EquipmentItem) => {
    if (!data) return;
    const existing = data.lines.find((l) => l.itemId === it.id);
    if (existing) return setLine(existing.key, { qty: existing.qty + 1 });
    update({ lines: [...data.lines, lineOf(it, data.payment.supportPercent)] });
  };

  const addCategory = (list: EquipmentItem[]) => {
    if (!data) return;
    const fresh = list.filter((it) => !data.lines.some((l) => l.itemId === it.id));
    update({
      lines: [...data.lines, ...fresh.map((it) => lineOf(it, data.payment.supportPercent))],
    });
  };

  const save = async (): Promise<QuoteView | null> => {
    if (!data) return null;
    setSaving(true);
    try {
      const r =
        isNew || !quote
          ? await equipmentApi.createQuote(data)
          : await equipmentApi.updateQuote(quote.id, data);
      setQuote(r.quote);
      setData(r.quote.data!);
      setDirty(false);
      if (isNew) navigate(`/equipment-quotes/${r.quote.id}`, { replace: true });
      notification.success({ message: `Đã lưu báo giá ${r.quote.code}` });
      return r.quote;
    } catch (e) {
      notification.error({ message: 'Chưa lưu được', description: errorText(e) });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const ensureSaved = async () => (dirty || !quote ? save() : quote);

  const exportImage = async () => {
    if (!data?.lines.length)
      return notification.info({ message: 'Chọn ít nhất một thiết bị trước.' });
    const q = await ensureSaved();
    if (!q) return;
    try {
      // Ảnh sản phẩm lấy qua server (PNG thu nhỏ) để canvas không bị "taint" bởi CDN khác nguồn.
      const keys = Array.from(
        new Set(data.lines.map((l) => l.imageKey).filter(Boolean))
      ) as string[];
      const thumbs = new Map<string, CanvasImageSource>();
      await Promise.all(
        keys.map(async (k) => {
          const b = await equipmentApi.thumbnail(k);
          if (b) thumbs.set(k, await createImageBitmap(b).catch(() => null as any));
        })
      );
      Array.from(thumbs.entries()).forEach(([k, v]) => !v && thumbs.delete(k));
      const blob = await renderQuoteImage({
        code: q.code,
        data,
        company: settings.company,
        thumbs,
      });
      triggerDownload(blob, `bao-gia-${q.code}.png`);
    } catch (e) {
      notification.error({ message: 'Không tạo được ảnh', description: errorText(e) });
    }
  };

  const exportExcel = async () => {
    if (!data?.lines.length)
      return notification.info({ message: 'Chọn ít nhất một thiết bị trước.' });
    const q = await ensureSaved();
    if (!q) return;
    try {
      await equipmentApi.exportExcel(q.id, q.code);
    } catch (e) {
      notification.error({ message: 'Không xuất được Excel', description: errorText(e) });
    }
  };

  const openContract = async () => {
    const q = await ensureSaved();
    if (q) setContractOpen(true);
  };

  const changeStatus = (status: QuoteStatus) => {
    if (!quote) return;
    const go = () =>
      equipmentApi
        .setStatus(quote.id, status)
        .then((r) => {
          setQuote(r.quote);
          notification.success({ message: `Đã chuyển sang "${STATUS_LABEL[status]}"` });
        })
        .catch((e) => notification.error({ message: 'Chưa đổi được', description: errorText(e) }));
    if (status === 'SIGNED')
      Modal.confirm({
        title: 'Đánh dấu đã ký hợp đồng?',
        content:
          'Sau khi đánh dấu, báo giá bị khoá (chỉ Admin mở lại được). Nhớ lưu bản hợp đồng đã ký (ảnh/PDF).',
        okText: 'Đã ký',
        cancelText: 'Huỷ',
        onOk: go,
      });
    else go();
  };

  if (!loaded) return <Spin />;
  if (!canUseEquipmentQuote) return <NoAccess />;
  if (error)
    return (
      <Result
        status="warning"
        title={error}
        extra={<Button onClick={() => navigate('/equipment-quotes')}>Về danh sách</Button>}
      />
    );
  if (!data || !summary) return <Spin />;

  const pay = data.payment;
  const ct = data.contract;
  const setPay = (patch: Partial<QuoteData['payment']>) =>
    update({ payment: { ...pay, ...patch } });
  const setCt = (patch: Partial<QuoteData['contract']>) =>
    update({ contract: { ...ct, ...patch } });
  const num = (
    v: number,
    onChange: (n: number) => void,
    min: number,
    max: number,
    suffix: string,
    /** Có placeholder: 0 hiện là ô trống (= để trống trong hợp đồng, tự thoả thuận). */
    placeholder?: string
  ) => (
    <InputNumber
      min={min}
      max={max}
      value={placeholder && !v ? null : v}
      placeholder={placeholder}
      addonAfter={suffix}
      style={{ width: 160 }}
      onChange={(n) => onChange(Number(n) || 0)}
    />
  );

  const columns = [
    {
      title: 'Thiết bị',
      dataIndex: 'name',
      width: 360,
      render: (_: unknown, l: QuoteLine) => (
        <div className={styles.lineName}>
          {l.imageKey ? (
            <img src={imageUrl(l.imageKey)} alt="" className={styles.thumb} />
          ) : (
            <span className={styles.thumbEmpty}>—</span>
          )}
          <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
            <Input
              value={l.name}
              disabled={locked}
              onChange={(e) => setLine(l.key, { name: e.target.value })}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {l.category}
            </Text>
          </Space>
        </div>
      ),
    },
    {
      title: 'Hãng',
      width: 130,
      render: (_: unknown, l: QuoteLine) => (
        <Input
          value={l.brand}
          placeholder="—"
          disabled={locked}
          style={{ width: 115 }}
          onChange={(e) => setLine(l.key, { brand: e.target.value })}
        />
      ),
    },
    {
      title: 'Tình trạng',
      width: 150,
      render: (_: unknown, l: QuoteLine) => (
        <Select
          value={l.condition ?? 'NEW'}
          disabled={locked}
          style={{ width: 140 }}
          onChange={(v) => setLine(l.key, { condition: v })}
          options={(['NEW', 'USED'] as const).map((v) => ({ value: v, label: CONDITION_LABEL[v] }))}
        />
      ),
    },
    {
      title: 'Đơn giá',
      width: 170,
      render: (_: unknown, l: QuoteLine) =>
        money(l.price, (n) => setLine(l.key, { price: n }), 160),
    },
    {
      title: 'SL',
      width: 80,
      render: (_: unknown, l: QuoteLine) => (
        <InputNumber
          min={1}
          max={999}
          value={l.qty}
          disabled={locked}
          style={{ width: 64 }}
          onChange={(n) => setLine(l.key, { qty: Number(n) || 1 })}
        />
      ),
    },
    {
      title: (
        <Tooltip title="Chọn ai trả món này. Tặng kèm: Creator HP Media miễn phí · Ứng trước: Công ty ứng tiền, Creator trả lại · Hỗ trợ %: Công ty trả một phần · Creator tự trả: 100%.">
          Hình thức thanh toán ⓘ
        </Tooltip>
      ),
      width: 250,
      render: (_: unknown, l: QuoteLine) => {
        const mode = payModeOf(l);
        return (
          <Space direction="vertical" size={4}>
            <Select<PayMode>
              value={mode}
              disabled={locked}
              style={{ width: 220 }}
              onChange={(m) => setLine(l.key, patchForMode(m, pay.supportPercent))}
              options={PAY_MODES.map((m) => ({
                value: m.value,
                label: <Tag color={m.color}>{m.label}</Tag>,
                disabled: m.value === 'FREE' && !data.hpCreator,
              }))}
            />
            {mode === 'SUPPORT' && (
              <InputNumber
                min={1}
                max={100}
                value={l.supportPercent}
                disabled={locked}
                addonBefore="Công ty"
                addonAfter="%"
                style={{ width: 220 }}
                onChange={(n) => setLine(l.key, { supportPercent: Number(n) || 0 })}
              />
            )}
          </Space>
        );
      },
    },
    {
      title: 'Thành tiền',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, l: QuoteLine) => {
        const a = lineAmounts(l);
        return (
          <div>
            <b>{formatVnd(a.amount)}</b>
            {a.creator > 0 && (
              <div className={styles.creatorPays}>Creator trả {formatVnd(a.creator)}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Serial',
      width: 150,
      render: (_: unknown, l: QuoteLine) => (
        <Input
          placeholder="Điền khi bàn giao"
          value={l.serial}
          onChange={(e) => setLine(l.key, { serial: e.target.value })}
        />
      ),
    },
    {
      width: 48,
      render: (_: unknown, l: QuoteLine) => (
        <Button
          type="text"
          danger
          disabled={locked}
          aria-label="Bỏ dòng"
          icon={<DeleteOutlined />}
          onClick={() => update({ lines: data.lines.filter((x) => x.key !== l.key) })}
        />
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <PageTitle
        title={quote ? `Báo giá ${quote.code}` : 'Báo giá mới'}
        extra={
          <Space wrap>
            {quote && (
              <Select
                value={quote.status}
                style={{ width: 170 }}
                onChange={changeStatus}
                options={QUOTE_STATUSES.map((s) => ({
                  value: s,
                  label: <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>,
                }))}
              />
            )}
            <Button icon={<PictureOutlined />} onClick={exportImage}>
              Xuất ảnh
            </Button>
            <Button icon={<FileExcelOutlined />} onClick={exportExcel}>
              Xuất Excel
            </Button>
            <Button icon={<FileProtectOutlined />} onClick={openContract}>
              Hợp đồng
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={locked}
              onClick={save}
            >
              {dirty || !quote ? 'Lưu' : 'Đã lưu'}
            </Button>
          </Space>
        }
      />
      {locked && (
        <Alert
          className={styles.gap}
          type="success"
          showIcon
          message="Hợp đồng đã ký — báo giá đã khoá. Admin có thể đổi trạng thái để mở lại."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={8}>
          <Card title="Danh mục thiết bị" size="small" className={styles.catalog}>
            <Input.Search
              placeholder="Tìm thiết bị…"
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {grouped.length === 0 ? (
              <Empty description="Chưa có thiết bị. Thêm ở tab Danh mục thiết bị." />
            ) : (
              <Collapse
                className={styles.gap}
                defaultActiveKey={grouped.map(([c]) => c)}
                size="small"
                items={grouped.map(([category, list]) => ({
                  key: category,
                  label: <b>{category}</b>,
                  extra: !locked && (
                    <Button
                      size="small"
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        addCategory(list);
                      }}
                    >
                      + cả nhóm
                    </Button>
                  ),
                  children: list.map((it) => {
                    const picked = data.lines.some((l) => l.itemId === it.id);
                    return (
                      <button
                        type="button"
                        key={it.id}
                        className={styles.pick}
                        disabled={locked}
                        onClick={() => addItem(it)}
                      >
                        {it.imageKey ? (
                          <img src={imageUrl(it.imageKey)} alt="" className={styles.thumbSm} />
                        ) : null}
                        <span className={styles.pickName}>{it.name}</span>
                        <span className={styles.pickPrice}>{priceText(it)}</span>
                        {picked ? <Tag color="green">Đã chọn</Tag> : <PlusOutlined />}
                      </button>
                    );
                  }),
                }))}
              />
            )}
            {!locked && (
              <Button
                block
                className={styles.gap}
                icon={<PlusOutlined />}
                onClick={() =>
                  update({
                    lines: [
                      ...data.lines,
                      {
                        key: newKey(),
                        category: 'Khác',
                        name: 'Món tự nhập',
                        price: 0,
                        qty: 1,
                        supportPercent: pay.supportPercent,
                      },
                    ],
                  })
                }
              >
                Thêm món ngoài danh mục
              </Button>
            )}
          </Card>
        </Col>

        <Col xs={24} xl={16}>
          <Card size="small" title={`Thiết bị đã chọn (${data.lines.length})`}>
            <Table
              rowKey="key"
              size="small"
              pagination={false}
              columns={columns}
              dataSource={data.lines}
              scroll={{ x: 1380 }}
              locale={{ emptyText: 'Bấm vào thiết bị bên trái để thêm vào báo giá' }}
            />
          </Card>

          <div className={styles.summary}>
            <div>
              <span>Tổng chi phí</span>
              <b>{formatVnd(summary.total)}</b>
            </div>
            <div className={styles.company}>
              <span>Công ty hỗ trợ ({summary.companyRate}%)</span>
              <b>{formatVnd(summary.company)}</b>
            </div>
            <div className={styles.creator}>
              <span>Creator trả</span>
              <b>{formatVnd(summary.creator)}</b>
              {summary.monthly > 0 && (
                <small>
                  trả trước {formatVnd(summary.upfront)} · {formatVnd(summary.monthly)}/tháng
                </small>
              )}
            </div>
            <div>
              <span>Phải hoàn nếu vi phạm</span>
              <b>{formatVnd(summary.refundable)}</b>
            </div>
          </div>

          <Card size="small">
            <Tabs
              items={[
                {
                  key: 'pay',
                  label: '1. Hỗ trợ & thanh toán',
                  children: (
                    <Row gutter={[16, 14]}>
                      <Col xs={24}>
                        <Space>
                          <Switch
                            checked={data.hpCreator}
                            disabled={locked}
                            onChange={(on) =>
                              update({
                                hpCreator: on,
                                // Bật lại: món tặng kèm về miễn phí 100%; tắt: tính theo % hỗ trợ chung.
                                lines: data.lines.map((l) =>
                                  l.included
                                    ? { ...l, supportPercent: on ? 100 : pay.supportPercent }
                                    : l
                                ),
                              })
                            }
                          />
                          <b>Creator của HP Media</b>
                          <span style={{ opacity: 0.7 }}>
                            — món "Tặng kèm" (thiết lập, quà, game tương tác) miễn phí
                          </span>
                        </Space>
                      </Col>
                      <Col xs={24} md={12}>
                        <div className={styles.fieldLabel}>
                          % Công ty hỗ trợ (áp cho các món đang bật hỗ trợ)
                        </div>
                        <Space>
                          {num(
                            pay.supportPercent,
                            (n) => setPay({ supportPercent: n }),
                            0,
                            100,
                            '%'
                          )}
                          <Button
                            disabled={locked}
                            onClick={() =>
                              update({
                                lines: data.lines.map((l) =>
                                  l.supportPercent > 0 && !l.included && !l.advance
                                    ? { ...l, supportPercent: pay.supportPercent }
                                    : l
                                ),
                              })
                            }
                          >
                            Áp cho tất cả
                          </Button>
                        </Space>
                      </Col>
                      <Col xs={24} md={12}>
                        <div className={styles.fieldLabel}>Creator trả trước khi nhận thiết bị</div>
                        {money(pay.upfrontAmount, (n) => setPay({ upfrontAmount: n }), 220)}
                      </Col>
                      <Col xs={24} md={12}>
                        <div className={styles.fieldLabel}>
                          Phần còn lại trả trong (0 = một lần)
                        </div>
                        {num(
                          pay.installmentMonths,
                          (n) => setPay({ installmentMonths: n }),
                          0,
                          60,
                          'tháng'
                        )}
                      </Col>
                      <Col xs={24} md={12}>
                        <div className={styles.fieldLabel}>Cách thanh toán</div>
                        <Select
                          style={{ width: 300 }}
                          value={pay.paymentMethod}
                          onChange={(v) => setPay({ paymentMethod: v })}
                          options={[
                            { value: 'DEDUCT_INCOME', label: 'Trừ dần vào thưởng / thu nhập' },
                            { value: 'CASH', label: 'Creator tự chuyển khoản' },
                            { value: 'MIXED', label: 'Chuyển khoản, trễ hạn thì trừ thu nhập' },
                          ]}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <div className={styles.fieldLabel}>
                          Chi phí đi lại / lắp đặt Công ty hỗ trợ (phải hoàn nếu vi phạm)
                        </div>
                        {money(
                          pay.travelSupportAmount,
                          (n) => setPay({ travelSupportAmount: n }),
                          220
                        )}
                      </Col>
                    </Row>
                  ),
                },
                {
                  key: 'creator',
                  label: '2. Thông tin Creator',
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }} size={14}>
                      <PersonFields value={data.creator} onChange={(p) => update({ creator: p })} />
                      <Space>
                        <Switch
                          checked={!!data.guardian}
                          onChange={(on) => update({ guardian: on ? { fullName: '' } : null })}
                        />
                        <span>Creator dưới 18 tuổi — cha / mẹ / người giám hộ ký cùng</span>
                      </Space>
                      {data.guardian && (
                        <PersonFields
                          guardian
                          value={data.guardian}
                          onChange={(p) => update({ guardian: p })}
                        />
                      )}
                      <Alert
                        type="info"
                        showIcon
                        message="Xem CCCD bản gốc để đối chiếu, chỉ lưu bản chụp. Không giữ CCCD gốc của Creator — việc đó trái luật."
                      />
                    </Space>
                  ),
                },
                {
                  key: 'contract',
                  label: '3. Điều khoản hợp đồng',
                  children: (
                    <Row gutter={[16, 14]}>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>
                          Số hợp đồng (để trống → tự đề xuất khi lưu)
                        </div>
                        <Space.Compact style={{ width: '100%' }}>
                          <Input
                            value={data.contractNo}
                            placeholder={quote ? suggestContractNo(quote.id) : 'Tự đề xuất khi lưu'}
                            onChange={(e) => update({ contractNo: e.target.value })}
                          />
                          <Button
                            disabled={!quote || locked}
                            onClick={() =>
                              quote && update({ contractNo: suggestContractNo(quote.id) })
                            }
                          >
                            Đề xuất
                          </Button>
                        </Space.Compact>
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>Ngày ký</div>
                        <Input
                          type="date"
                          value={data.signDate}
                          onChange={(e) => update({ signDate: e.target.value })}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>Nơi ký</div>
                        <Input
                          value={data.signPlace}
                          onChange={(e) => update({ signPlace: e.target.value })}
                        />
                      </Col>
                      <Col xs={24}>
                        <div className={styles.fieldLabel}>Địa chỉ lắp đặt thiết bị</div>
                        <Input
                          value={data.deliveryAddress}
                          onChange={(e) => update({ deliveryAddress: e.target.value })}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>
                          Thời gian cho mượn / cam kết (tối đa 12)
                        </div>
                        {num(ct.commitMonths, (n) => setCt({ commitMonths: n }), 1, 12, 'tháng')}
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>
                          "Có hoạt động" = mỗi tháng LIVE ≥ (để trống = tự thoả thuận)
                        </div>
                        <Space>
                          {num(
                            ct.minLiveDaysPerMonth,
                            (n) => setCt({ minLiveDaysPerMonth: n }),
                            0,
                            31,
                            'ngày',
                            'VD: 20'
                          )}
                          {num(
                            ct.minLiveHoursPerMonth,
                            (n) => setCt({ minLiveHoursPerMonth: n }),
                            0,
                            744,
                            'giờ',
                            'VD: 90'
                          )}
                        </Space>
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>
                          Không hoạt động liên tục → hoàn trả toàn bộ
                        </div>
                        {num(
                          ct.inactivityMonths,
                          (n) => setCt({ inactivityMonths: n }),
                          1,
                          12,
                          'tháng'
                        )}
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>Không liên lạc được → vi phạm</div>
                        {num(
                          ct.unreachableDays,
                          (n) => setCt({ unreachableDays: n }),
                          1,
                          60,
                          'ngày'
                        )}
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>
                          Hạn trả lại thiết bị / tiền sau thông báo
                        </div>
                        {num(ct.returnDays, (n) => setCt({ returnDays: n }), 1, 60, 'ngày')}
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>
                          <Tooltip title="Luật Dân sự cho tự thoả thuận; để 8% cho an toàn nếu Creator bị coi là kinh doanh (Luật Thương mại trần 8%). Ngoài tiền phạt, hợp đồng vẫn đòi bồi thường thiệt hại thực tế.">
                            Phạt vi phạm (% phần bị vi phạm) ⓘ
                          </Tooltip>
                        </div>
                        {num(ct.penaltyPercent, (n) => setCt({ penaltyPercent: n }), 0, 100, '%')}
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>Lãi chậm trả (tối đa 20%/năm)</div>
                        {num(
                          ct.lateInterestPercentYear,
                          (n) => setCt({ lateInterestPercentYear: n }),
                          0,
                          20,
                          '%/năm'
                        )}
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>
                          <Tooltip title="Trích % thu nhập LIVE của tài khoản cá nhân để TRẢ DẦN tiền thiết bị; trả đủ thì dừng. Để trống = hai bên tự điền vào hợp đồng.">
                            Trích thu nhập LIVE để trả dần ⓘ
                          </Tooltip>
                        </div>
                        {num(
                          ct.incomeSharePercent,
                          (n) => setCt({ incomeSharePercent: n }),
                          0,
                          100,
                          '%',
                          'Để trống'
                        )}
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>
                          <Tooltip title="Chỉ khi MẤT hoặc HỎNG KHÔNG ĐƯỢC BẢO HÀNH (rơi rớt, vào nước…). Trả lại bình thường không trừ gì.">
                            Mức đền theo giá hoá đơn: mới / cũ ⓘ
                          </Tooltip>
                        </div>
                        <Space>
                          {num(
                            ct.compensateNewPercent,
                            (n) => setCt({ compensateNewPercent: n }),
                            0,
                            100,
                            '% mới'
                          )}
                          {num(
                            ct.compensateUsedPercent,
                            (n) => setCt({ compensateUsedPercent: n }),
                            0,
                            100,
                            '% cũ'
                          )}
                        </Space>
                      </Col>
                      <Col xs={24} md={8}>
                        <div className={styles.fieldLabel}>
                          Thời hạn gắn Đại lý trên TikTok (0 = không ghi)
                        </div>
                        {num(
                          ct.agencyTermYears,
                          (n) => setCt({ agencyTermYears: n }),
                          0,
                          10,
                          'năm'
                        )}
                      </Col>
                      <Col xs={24}>
                        <Space direction="vertical">
                          <Space>
                            <Switch
                              checked={ct.allowBuyout}
                              onChange={(v) => setCt({ allowBuyout: v })}
                            />
                            Creator được đề nghị mua đứt 100% giá hoá đơn, trả nhiều đợt theo nhu
                            cầu
                          </Space>
                          <Space>
                            <Switch
                              checked={ct.leaveAgencyIsBreach}
                              onChange={(v) => setCt({ leaveAgencyIsBreach: v })}
                            />
                            Rời Đại lý trên TikTok khi còn mượn thiết bị = vi phạm (phải trả thiết
                            bị)
                          </Space>
                          <Space>
                            <Switch
                              checked={ct.ownershipTransfer}
                              onChange={(v) => setCt({ ownershipTransfer: v })}
                            />
                            Hết cam kết, không vi phạm → thiết bị hỗ trợ thuộc về Creator
                          </Space>
                          <Space>
                            <Switch
                              checked={ct.allowDeductIncome}
                              onChange={(v) => setCt({ allowDeductIncome: v })}
                            />
                            Creator đồng ý cho Công ty trừ khoản phải trả / phải hoàn vào thưởng,
                            thu nhập
                          </Space>
                          <Space>
                            <Switch
                              checked={ct.disputeAtCompanyCourt}
                              onChange={(v) => setCt({ disputeAtCompanyCourt: v })}
                            />
                            Tranh chấp xử ở Toà án nơi Công ty có trụ sở (không phải đi kiện ở quê
                            Creator)
                          </Space>
                        </Space>
                      </Col>
                      <Col xs={24}>
                        <div className={styles.fieldLabel}>
                          Thoả thuận khác (mỗi dòng một ý — in thành điều riêng)
                        </div>
                        <Input.TextArea
                          rows={3}
                          value={data.extraClauses}
                          onChange={(e) => update({ extraClauses: e.target.value })}
                        />
                      </Col>
                    </Row>
                  ),
                },
                {
                  key: 'notes',
                  label: '4. Lưu ý trên báo giá',
                  children: (
                    <Input.TextArea
                      rows={4}
                      value={data.notes}
                      onChange={(e) => update({ notes: e.target.value })}
                    />
                  ),
                },
                {
                  key: 'handover',
                  label: (
                    <span>
                      5. Bàn giao (KTV){' '}
                      {data.handover?.confirmedAt ? (
                        <Tag color="green" style={{ marginInlineStart: 4 }}>
                          Đã bàn giao
                        </Tag>
                      ) : null}
                    </span>
                  ),
                  children: (
                    <HandoverTab
                      quote={quote}
                      data={data}
                      company={settings.company}
                      currentUserName={displayName}
                      onSaved={(q) => {
                        setQuote(q);
                        // Chỉ thay phần bàn giao: KTV có thể đang sửa dở phần khác của báo giá.
                        setData((d) => (d ? { ...d, handover: q.data?.handover ?? null } : d));
                      }}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {quote && (
        <ContractModal
          open={contractOpen}
          onClose={() => setContractOpen(false)}
          code={quote.code}
          data={data}
          company={settings.company}
        />
      )}
    </div>
  );
};

export default EquipmentQuoteEditor;
